import { addDays, parseISO, format, differenceInCalendarDays } from 'date-fns'
import config from 'isomorphic-config'
import pclnConfig from '@pcln/config'
import analytics from '../../utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '../../constants/analytics'
import {
  CrossSellContentBlock,
  Booking,
  CarInventoryParams,
  HotelInventoryParams,
  Product
} from './types'
import cleanSlashes from '../../utils/clean-slashes'

const hosts = pclnConfig.hosts ?? {}
const baseUrl = hosts.baseUrl || '/'
const baseUrlSecure = hosts.baseUrlSecure || '/'
type ServiceConfig = {
  uri: string
  secure: boolean
}
const services: Record<string, ServiceConfig> = pclnConfig.services ?? {}
const itineraryServiceConfig = config.client['itinerary-service']

export const getLinkUri = (serviceName: string, endpoint: string) => {
  const service = services[serviceName]
  if (!service) {
    throw new Error(`service ${serviceName} not configured`)
  }
  const base = service.secure ? baseUrlSecure : baseUrl
  return `${cleanSlashes(base)}/${cleanSlashes(service.uri)}${
    endpoint ? `/${cleanSlashes(endpoint)}` : ''
  }`
}

const PCLN_ITINERARY_SERVICE_URL =
  itineraryServiceConfig.url || getLinkUri('itinerary-service', 'graph')
const PCLN_ITINERARY_SERVICE_TIMEOUT = itineraryServiceConfig.timeout

const serviceOptions = {
  url: PCLN_ITINERARY_SERVICE_URL,
  method: 'POST',
  timeout: PCLN_ITINERARY_SERVICE_TIMEOUT
}

export function buildContent(booking: Booking): CrossSellContentBlock {
  const startDate = parseISO(booking.travelStartDateTime)
  const endDate = parseISO(booking.travelEndDateTime)
  const formattedDate = `${format(startDate, 'EEE, MMM dd')} - ${format(
    endDate,
    'EEE, MMM dd'
  )}`

  const hotelsXSellUrl = (destination: string, start: Date, end: Date) =>
    `/relax/in/${destination}/from/${format(start, 'yyyyMMdd')}/to/${format(
      end,
      'yyyyMMdd'
    )}/rooms/1`

  switch (booking.productId) {
    case Product.Hotel: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'hotel',
        xSellMessaging: 'Your hotel booking unlocks up to 40% off rental cars',
        xSellTarget: 'Rental Cars',
        date: formattedDate,
        headline: booking.destinationCity,
        subHeadline: `Check-in on ${format(checkInDate, 'EEE, MMM dd')}`,
        xSellUrl: `/drive/search/r/${booking.destinationCity}/${
          booking.destinationCity
        }/${format(startDate, 'yyyyMMdd')}-12:00/${format(
          endDate,
          'yyyyMMdd'
        )}-12:00/list`
      }
    }
    case Product.Flight: {
      const destination =
        booking.destinationCity || booking.destinationLocationCode

      const isSameDay = differenceInCalendarDays(endDate, startDate) < 1
      return {
        product: 'flight',
        xSellMessaging:
          'Your flight booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        date: formattedDate,
        headline: `${booking.originLocationCode} - ${booking.destinationLocationCode}`,
        subHeadline: `Departs ${format(startDate, "EEE, MMM dd 'at' h:mm b")}`,
        xSellUrl: hotelsXSellUrl(
          destination,
          startDate,
          isSameDay ? addDays(endDate, 1) : endDate
        )
      }
    }
    case Product.Car: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'car',
        xSellMessaging:
          'Your rental car booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        date: formattedDate,
        headline: `${booking.destinationCity}`,
        subHeadline: `Pickup on ${format(checkInDate, 'EEE, MMM do')}`,
        xSellUrl: hotelsXSellUrl(booking.destinationCity, startDate, endDate)
      }
    }
    // no default
  }
}

export function buildRCInventoryRequest({
  pickupDateTime,
  returnDateTime,
  pickupLocation,
  returnLocation,
  offerToken,
  tokenType
}: CarInventoryParams) {
  return {
    ...serviceOptions,
    data: {
      query: `{
        rcXsell(
          pickupDateTime: "${pickupDateTime}",
          returnDateTime: "${returnDateTime}",
          pickupLocation: "${pickupLocation}",
          returnLocation: "${returnLocation}",
          offerToken: "${offerToken}",
          tokenType: "${tokenType}"
        ) {
          listings: content {
            id
            checkoutUrl
            carExample
            isCarExampleExact
            pricing {
              price
              currencySymbol
              disclaimer
              priceDescription
            }
            partner {
              name
              url
            }
            options
            bannerType
            carImageUrl
            vehicleFeatures {
              bagCapacity
              isAirConditioned
              isUnlimitedMileage
              mileageDistance
              mileageUnit
              peopleCapacity
            }
            rate {
              currencyCode
              currencySymbol
              dailyPrice
              isFreeCancellation
              isPrepay
              savingsPriceTotal
              strikeThroughPrice
              totalPrice
            }
          }
        }
      }`
    }
  }
}

export function buildHotelInventoryRequest({
  cityId,
  cityName,
  checkIn,
  checkOut,
  cguid,
  appc,
  rid,
  productId,
  pageSize = 3,
  dealTypes
}: HotelInventoryParams) {
  return {
    ...serviceOptions,
    data: {
      variables: {
        cityId,
        cityName,
        checkIn,
        checkOut,
        cguid,
        appc,
        rid,
        productId,
        pageSize,
        dealTypes
      },
      query: `{
        integratedListings(
          cityId: ${cityId}
          cityName: "${cityName}"
          checkIn: ${checkIn}
          checkOut: ${checkOut}
          appc: "${appc}"
          cguid: "${cguid}"
          rid: "${rid}"
          productId: ${productId}
          pageSize: ${pageSize}
        ) {
          listings {
            name
            location
            imageUrl
            guestRating
            guestReviewCount
            starRating
            savingPercentage
            originalPrice
            currentPrice
            currency
            currencySymbol
            hotelId
            href
          }
        }
      }`
    }
  }
}

export function buildItineraryDetailsRequest(
  offerToken: string,
  tokenType: string
) {
  return {
    ...serviceOptions,
    data: {
      query: `{
        itineraryDetails(offerToken: "${offerToken}", tokenType: "${tokenType}") {
          hotelXsellCarousel {
            checkIn
            checkOut
            cityId
            cityName
          }
          rcXsellRequestParams {
            pickupDateTime
            returnDateTime
            pickupLocation
            returnLocation
          }
        }
      }`
    }
  }
}

export function trackCrossSellClicks() {
  analytics.fireEvent({
    action: 'Feature Box CTA',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell Choose Button'
  })
}
